import { identical, zip } from 'ramda';

export function isArrayEqual<T>(
  left: T[],
  right: T[],
  compare: (a: T, b: T) => boolean = identical,
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return zip(left, right).every(([a, b]) => compare(a, b));
}

export function isNotNull<T>(input: T | undefined | null): input is T {
  return input != null;
}

export function isNullOrEqual<T>(
  l: T | null | undefined,
  r: T | null | undefined,
): boolean {
  return (l == null && r == null) || l === r;
}

export function isSetsEqual(l: Set<any>, r: Set<any>): boolean {
  if (l.size !== r.size) return false;
  return Array.from(l).every(i => r.has(i));
}

export function randomElement<T>(array: readonly T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

export function randomValue(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export function randomInteger(min: number, max: number) {
  return Math.round(randomValue(min, max));
}

export function optionally<T, U>(
  ifNotNull: (i: T) => U,
  input: T | null | undefined,
): U | undefined {
  if (input == null) {
    return undefined;
  }
  return ifNotNull(input);
}

export function maybe<T>(input: T): T | undefined {
  return input;
}

export async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function roundTo(input: number, decimal = 2) {
  const by = Math.pow(10, decimal);
  return Math.round(input * by) / by;
}

// this is meant to be used to extract IDs
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function testId(id: string, msg?: string) {
  return id;
}

export function splice<T>(
  array: T[],
  ...spliceArgs: Parameters<Array<T>['splice']>
) {
  const copy = array.slice();
  copy.splice(...spliceArgs);
  return copy;
}

export function lastElement<T>(array: T[]): T {
  return array[array.length - 1];
}

function stableArraySort<T>(
  comparator: (item1: T, item2: T) => number,
  items: T[],
): T[] {
  const newItems = items.slice();

  const itemOrders = new Map<T, number>();
  items.forEach((item, idx) => {
    itemOrders.set(item, idx);
  });

  newItems.sort((a, b) => {
    const res = comparator(a, b);
    if (res !== 0) return res;

    const aIdx = itemOrders.get(a)!;
    const bIdx = itemOrders.get(b)!;
    if (aIdx < bIdx) return -1;
    if (aIdx > bIdx) return 1;
    return 0;
  });

  return newItems;
}
function builtinArraySort<T>(
  comparator: (item1: T, item2: T) => number,
  items: T[],
): T[] {
  const newItems = items.slice();
  newItems.sort(comparator);
  return newItems;
}
export const sort =
  // https://github.com/facebook/hermes/issues/212
  // prettier-ignore
  !!(global as any)?.HermesInternal ? stableArraySort :
  builtinArraySort;

export function sortBy<T>(
  iteratee: (item: T) => any,
): (items: readonly T[]) => T[];
export function sortBy<T>(iteratee: (item: T) => any, items: readonly T[]): T[];
export function sortBy<T>(
  iteratee: (item: T) => any,
  items?: readonly T[],
): any {
  if (items == null) {
    return (items: T[]) => sortBy(iteratee, items);
  }

  return sortBy(iteratee, items);

  // https://github.com/ramda/ramda/blob/ed191e6a476330e37db259a5c2d04bfba0b2d63d/source/sortBy.js
  function sortBy(iteratee: (item: T) => any, items: readonly T[]): any {
    return sort((a, b) => {
      const aa = iteratee(a);
      const bb = iteratee(b);
      return aa < bb ? -1 : aa > bb ? 1 : 0;
    }, items as T[]);
  }
}

export function isShallowEqual(
  objA: any,
  objB: any,
  compare?: (
    objValue: any,
    otherValue: any,
    key?: string,
  ) => boolean | undefined,
  compareContext?: any,
): boolean {
  let ret = compare ? compare.call(compareContext, objA, objB) : undefined;

  if (ret !== undefined) return Boolean(ret);

  if (Object.is(objA, objB)) return true;

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  for (let i = 0; i < keysA.length; i++) {
    const key = keysA[i];

    if (!Object.prototype.hasOwnProperty.call(objB, key)) {
      return false;
    }

    const valueA = objA[key];
    const valueB = objB[key];

    ret = compare
      ? compare.call(compareContext, valueA, valueB, key)
      : undefined;
    if (ret !== undefined) return Boolean(ret);

    if (!Object.is(valueA, valueB)) {
      return false;
    }
  }

  return true;
}

export function shuffle<T>(input: readonly T[]): T[] {
  return input
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(a => a.value);
}

export function retry<T>(
  action: (attempt: number) => Promise<T>,
  times = 3,
  strategy:
    | 'exponential-backoff'
    | 'fixed'
    | ((count: number, error: unknown) => number) = 'exponential-backoff',
): Promise<T> {
  return new Promise((resolve, reject) => {
    let count = 0;
    const retryAction = () => {
      action(count)
        .then(resolve)
        .catch(err => {
          if (count >= times) {
            reject(err);
          } else {
            count++;
            const delay =
              typeof strategy === 'function'
                ? strategy(count, err)
                : strategy === 'exponential-backoff'
                  ? 1000 * Math.pow(2, count)
                  : 1000;
            void sleep(delay).then(retryAction);
          }
        });
    };
    retryAction();
  });
}

export function asciiString(input: string) {
  return input.replace(/[^\x00-\xFF]/g, '');
}

export function arrayJoin<T>(
  separator: (info: { index: number }) => T,
  inputArray: T[],
): T[] {
  return inputArray.reduce((outputArray: T[], child, index) => {
    outputArray.push(child);

    if (index < inputArray.length - 1) {
      outputArray.push(separator({ index }));
    }

    return outputArray;
  }, []);
}

export function hasAny<T>(ary: null | undefined | T[]): ary is [T, ...T[]] {
  return ary != null && ary.length > 0;
}

export function truncate(input: string, length: number) {
  if (input.length <= length) {
    return input;
  }
  return input.substring(0, length) + '...';
}

export const uniqueIdFactory = (prefix = '') => {
  let id = -1;

  return () => {
    id += 1;
    return prefix + id;
  };
};

export const noop = (): void => {};
